import type { PortalRecord } from '../api';

const writableContactFields = [
  'Contact Name', 'Title/Position', 'Email Address', 'Phone Number', 'Alternate Phone',
  'Address', 'Preferred Contact Method', 'Notes',
] as const;

function text(value: unknown): string {
  return typeof value === 'string' ? value.trim() : '';
}

export function relationItems(value: unknown): Array<{ id: string; label: string }> {
  if (!Array.isArray(value)) return [];
  return value.flatMap(item => {
    if (typeof item === 'string' && item.startsWith('rec')) return [{ id: item, label: item }];
    if (typeof item !== 'object' || item === null) return [];
    const id = text((item as { id?: unknown }).id);
    if (!id.startsWith('rec')) return [];
    return [{ id, label: text((item as { label?: unknown }).label) || id }];
  });
}

export function contactEmailHref(value: unknown): string | undefined {
  const email = text(value);
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email) ? `mailto:${email}` : undefined;
}

export function contactPhoneHref(value: unknown): string | undefined {
  const phone = text(value);
  const dialable = phone.replace(/[^\d+]/g, '');
  return dialable.length >= 7 ? `tel:${dialable}` : undefined;
}

export function isMainPoc(record: PortalRecord): boolean {
  return relationItems(record.fields['Clients (Main POC)']).length > 0;
}

export function findDuplicateEmail(records: PortalRecord[], emailValue: unknown, currentId?: string): PortalRecord | undefined {
  const email = text(emailValue).toLowerCase();
  if (!email) return undefined;
  return records.find(record => record.id !== currentId && text(record.fields['Email Address']).toLowerCase() === email);
}

/** Build a Contact write payload while protecting Airtable's reverse Main POC field. */
export function buildContactWriteFields(draft: Record<string, unknown>, linkedClientId: string, editing: boolean): Record<string, unknown> {
  const fields: Record<string, unknown> = { 'Linked Client': linkedClientId ? [linkedClientId] : [] };
  for (const name of writableContactFields) {
    const value = draft[name];
    if (typeof value === 'string') {
      const normalized = value.trim();
      if (normalized) fields[name] = normalized;
      else if (editing) fields[name] = null;
    } else if (value !== undefined && value !== null) fields[name] = value;
  }
  return fields;
}
